import { rankedSearch } from "@/lib/search";
// معالج تقارير موحّد (مسار/تميز): نوع ← نطاق ← فترة ← محاور وتفصيل ← توليد خلفي ← معاينة الملف نفسه وتنزيله
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import { fileUrl } from "@/v2/lib/files";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { PageHead } from "@/v2/admin/ui";
import { fmtNum } from "@/masar/lib";
import { FileText, Layers, SlidersHorizontal, Calendar, ListChecks, Download, RefreshCw, AlertTriangle, CheckCircle2, Clock, ChevronRight, ChevronLeft, Eye, X } from "lucide-react";

const STEPS = [["kind", "نوع التقرير", Layers], ["scope", "النطاق", SlidersHorizontal], ["period", "الفترة", Calendar], ["sections", "المحاور والتفصيل", ListChecks], ["result", "المعاينة والتنزيل", Eye]];
const DETAILS = [["summary", "ملخص تنفيذي", "مؤشرات ونتائج موجزة"], ["detailed", "تفصيلي", "الملخص مع الجداول الكاملة لكل محور"], ["records", "تفصيلي مع سجلات الموظفين", "يضيف الصفوف الفردية للمخولين برؤيتها"]];
const PERIODS = [
  ["cumulative", "حالة تراكمية حتى الآن", "الوضع الافتراضي: كل السجلات حتى وقت الإصدار."],
  ["as_of", "حالة حتى تاريخ", "لقطة الحالة كما كانت حتى التاريخ المختار."],
  ["period", "حركة خلال فترة", "الأحداث الواقعة داخل الفترة (التحاقات، محاولات، إتمامات). الحالة تبقى تراكمية."],
  ["cohort", "فوج التحاق بين تاريخين", "نسب البدء والإكمال للأساسي تُحسب لمن التحق بين التاريخين."],
];
const EMPTY_FILTERS = { as_of: "", period_from: "", period_to: "", cohort_from: "", cohort_to: "" };

export const Sel = ({ label, value, onChange, options, testId, placeholder = "— الكل —", disabled }) => (
  <div>
    <label className="v2-label">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="v2-input" data-testid={testId} disabled={disabled}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/** سلسلة الوحدات التنظيمية (قطاع ← إدارة عامة ← إدارة ← قسم) */
export const UnitCascade = ({ units, unitId, onChange, prefix = "rf" }) => {
  const chain = useMemo(() => {
    const out = []; let cur = units.find((u) => u.id === unitId);
    while (cur) { out.unshift(cur); cur = units.find((u) => u.id === cur.parent_id); }
    return out;
  }, [unitId, units]);
  const at = (k) => chain.find((u) => u.kind === k)?.id || "";
  const byKind = (k, parent) => units.filter((u) => u.kind === k && (!parent || u.parent_id === parent));
  const gd = byKind("general_department", at("sector")), dep = byKind("department", at("general_department") || undefined), sec = byKind("section", at("department") || undefined);
  const pick = (id, fallback) => onChange(id || fallback || "");
  return (
    <>
      <Sel label="القطاع" value={at("sector")} onChange={(v) => pick(v)} options={byKind("sector").map((u) => ({ value: u.id, label: u.name }))} testId={`${prefix}-sector`} placeholder="— كل القطاعات —" />
      <Sel label="الإدارة العامة" value={at("general_department")} onChange={(v) => pick(v, at("sector"))} options={gd.map((u) => ({ value: u.id, label: u.name }))} testId={`${prefix}-gd`} disabled={!gd.length} placeholder={gd.length ? "— الكل —" : "لا توجد"} />
      <Sel label="الإدارة" value={at("department")} onChange={(v) => pick(v, at("general_department") || at("sector"))} options={dep.map((u) => ({ value: u.id, label: u.name }))} testId={`${prefix}-dep`} disabled={!dep.length} placeholder={dep.length ? "— الكل —" : "لا توجد"} />
      <Sel label="القسم" value={at("section")} onChange={(v) => pick(v, at("department"))} options={sec.map((u) => ({ value: u.id, label: u.name }))} testId={`${prefix}-sec`} disabled={!sec.length} placeholder={sec.length ? "— الكل —" : "لا توجد"} />
    </>
  );
};

export const EmployeePicker = ({ employees, value, onChange, testId = "rf-emp" }) => {
  const [q, setQ] = useState("");
  const list = rankedSearch(employees || [], q, e => [e.name, e.job_title]).slice(0, 80);
  return (
    <div className="sm:col-span-2">
      <label className="v2-label">موظف محدد (تقرير موظف مفصل)</label>
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم…" className="v2-input !w-40" data-testid={`${testId}-search`} />
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="v2-input flex-1" data-testid={testId}><option value="">— كل الموظفين في النطاق —</option>{list.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
      </div>
    </div>
  );
};

function PeriodStep({ mode, setMode, f, setF }) {
  const D = ({ k, label, testId }) => <div><label className="v2-label">{label}</label><input type="date" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="v2-input" dir="ltr" data-testid={testId} /></div>;
  return (
    <div className="space-y-4" data-testid="step-period">
      <div className="grid sm:grid-cols-2 gap-3">
        {PERIODS.map(([k, l, d]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`rp-${k}`} className={`text-start v2-inset p-4 transition-all ${mode === k ? "!border-beige/70 !bg-beige/12" : "hover:!border-line"}`}>
            <div className="font-bold text-sm flex items-center gap-2">{mode === k && <CheckCircle2 size={14} className="text-beige-ink" />} {l}</div>
            <div className="text-xs text-ink-2 mt-1 leading-relaxed">{d}</div>
          </button>
        ))}
      </div>
      {mode === "as_of" && <div className="grid sm:grid-cols-3 gap-3"><D k="as_of" label="تاريخ القياس" testId="rp-as-of" /></div>}
      {mode === "period" && <div className="grid sm:grid-cols-3 gap-3"><D k="period_from" label="من" testId="rp-from" /><D k="period_to" label="إلى" testId="rp-to" /></div>}
      {mode === "cohort" && <div className="grid sm:grid-cols-3 gap-3"><D k="cohort_from" label="التحق من" testId="rp-cfrom" /><D k="cohort_to" label="إلى" testId="rp-cto" /><D k="as_of" label="تاريخ الرصد (اختياري)" testId="rp-cas" /></div>}
    </div>
  );
}

function SectionsStep({ s, setS, sections, fullDesc }) {
  const toggle = (k) => setS({ ...s, sections: s.sections.includes(k) ? s.sections.filter((x) => x !== k) : [...s.sections, k] });
  return (
    <div className="space-y-5" data-testid="step-sections">
      {s.kind === "custom" ? (
        <div>
          <div className="font-bold text-sm mb-2">المحاور المطلوبة</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sections.map((x) => (
              <label key={x.key} className={`v2-inset p-3 flex items-center gap-3 cursor-pointer text-sm ${s.sections.includes(x.key) ? "!border-beige/60" : ""}`} data-testid={`rs-${x.key}`}>
                <input type="checkbox" className="w-4 h-4 accent-[#006654]" checked={s.sections.includes(x.key)} onChange={() => toggle(x.key)} /> {x.label}
              </label>
            ))}
          </div>
          <div className="text-xs text-ink-2 mt-2">يُضاف «دليل القراءة» المختصر تلقائيًا في نهاية كل تقرير.</div>
        </div>
      ) : <div className="v2-inset p-4 text-sm text-ink-2">{fullDesc}</div>}
      <div>
        <div className="font-bold text-sm mb-2">مستوى التفصيل</div>
        <div className="grid sm:grid-cols-3 gap-2">
          {DETAILS.map(([k, l, d]) => (
            <button key={k} onClick={() => setS({ ...s, detail: k })} data-testid={`rd-${k}`} className={`text-start v2-inset p-3 ${s.detail === k ? "!border-beige/70 !bg-beige/12" : ""}`}>
              <div className="font-bold text-sm">{l}</div><div className="text-xs text-ink-2 mt-0.5">{d}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultPanel({ job, fileBase, onRegenerate, busy }) {
  const [left, setLeft] = useState(job?.expires_in ?? null);
  useEffect(() => { setLeft(job?.expires_in ?? null); }, [job?.id, job?.expires_in]);
  useEffect(() => {
    if (left == null || left <= 0) return undefined;
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [left != null && left > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!job) return null;
  const expired = job.status === "expired" || (job.status === "ready" && left === 0);
  const src = fileUrl(`${fileBase}/${job.id}/file`);
  const dl = `${src}&download=1`;
  return (
    <div className="space-y-4" data-testid="report-result">
      {(job.status === "queued" || job.status === "running") && (
        <GlassCard className="p-5" data-testid="report-progress">
          <div className="flex items-center gap-3 mb-3"><RefreshCw size={18} className="animate-spin text-beige-ink" /><div><div className="font-bold">جارٍ توليد التقرير في الخلفية</div><div className="text-xs text-ink-2">{job.stage} — يمكنك تحديث الصفحة دون فقد الطلب.</div></div><b className="ms-auto tabular-nums" dir="ltr">{job.progress}%</b></div>
          <div className="h-2 rounded-full bg-surface-3 overflow-hidden"><div className="h-full bg-beige transition-all duration-500" style={{ width: `${job.progress}%` }} /></div>
        </GlassCard>
      )}
      {job.status === "failed" && (
        <GlassCard className="p-5 flex items-center gap-3 flex-wrap" data-testid="report-failed">
          <AlertTriangle className="text-danger" /><div className="flex-1 min-w-[200px]"><div className="font-bold">تعذّر توليد التقرير</div><div className="text-xs text-ink-2">{job.error || "خطأ غير متوقع"}</div></div>
          <button onClick={onRegenerate} disabled={busy} className="v2-btn v2-btn-sm" data-testid="report-retry"><RefreshCw size={13} /> إعادة المحاولة</button>
        </GlassCard>
      )}
      {expired && (
        <GlassCard className="p-5 flex items-center gap-3 flex-wrap" data-testid="report-expired">
          <Clock className="text-beige-ink" /><div className="flex-1 min-w-[200px]"><div className="font-bold">انتهت صلاحية الملف المؤقت</div><div className="text-xs text-ink-2">يُحفظ الملف 10 دقائق من جاهزيته فقط ولا يُؤرشف. أعد التوليد بنفس الإعدادات للحصول على نسخة محدّثة.</div></div>
          <button onClick={onRegenerate} disabled={busy} className="v2-btn v2-btn-sm" data-testid="report-regenerate"><RefreshCw size={13} /> إعادة التوليد</button>
        </GlassCard>
      )}
      {job.status === "ready" && !expired && (
        <>
          <GlassCard className="p-4 flex items-center gap-3 flex-wrap" data-testid="report-ready">
            <CheckCircle2 className="text-ok" />
            <div className="flex-1 min-w-[220px]"><div className="font-bold text-sm">التقرير جاهز — {job.scope_label}</div><div className="text-xs text-ink-2">الحجم <span dir="ltr">{(job.size / 1024).toFixed(0)} KB</span> · تنتهي صلاحية الملف المؤقت بعد <b className="text-ink tabular-nums" dir="ltr">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</b> · المعاينة هي الملف نفسه الذي سيُنزَّل</div></div>
            <a href={dl} className="v2-btn v2-btn-sm" data-testid="report-download"><Download size={13} /> تنزيل PDF</a>
            <a href={src} target="_blank" rel="noreferrer" className="v2-btn-ghost v2-btn-sm" data-testid="report-open-tab">فتح في تبويب</a>
            <button onClick={onRegenerate} disabled={busy} className="v2-btn-ghost v2-btn-sm" data-testid="report-regenerate-ready"><RefreshCw size={13} /> إعادة التوليد</button>
          </GlassCard>
          <div className="rounded-2xl overflow-hidden border border-line bg-surface" style={{ height: "78vh" }}>
            <iframe title="معاينة التقرير" src={`${src}#view=FitH`} className="w-full h-full" data-testid="report-preview" />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * props: title, sub, testId, storageKey, optionsUrl, jobsUrl (api path بلا /api), fileBase (مع /api للـ iframe),
 * renderScope({f, setF, opt}), chips(opt, f) => string[], sections(opt) => [{key,label}], fullDesc, kinds
 */
export default function ReportWizard({ title, sub, testId, storageKey, optionsUrl, jobsUrl, fileBase, renderScope, chips, sections, fullDesc, kinds, icon = FileText }) {
  const [opt, setOpt] = useState(null);
  const [optionsError, setOptionsError] = useState(false);
  const timer = useRef(null);
  const activeJob = useRef(null);
  const mounted = useRef(true);
  const [step, setStep] = useState(0);
  const EMPTY = useMemo(() => ({ kind: "full", sections: [], detail: "detailed", period_mode: "cumulative", filters: { ...EMPTY_FILTERS } }), []);
  const [s, setS] = useState(EMPTY);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const setF = (filters) => setS({ ...s, filters });

  useEffect(() => { api.get(optionsUrl).then((r) => setOpt(r.data)).catch((e) => { setOptionsError(true); toast.error(errText(e)); }); }, [optionsUrl]);

  const payload = useMemo(() => {
    const f = { ...s.filters };
    if (s.period_mode !== "as_of" && s.period_mode !== "cohort") f.as_of = "";
    if (s.period_mode !== "period") { f.period_from = ""; f.period_to = ""; }
    if (s.period_mode !== "cohort") { f.cohort_from = ""; f.cohort_to = ""; }
    return { kind: s.kind, sections: s.kind === "custom" ? s.sections : [], detail: s.detail, filters: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
  }, [s]);

  const poll = async (id, failures = 0) => {
    if (!mounted.current || activeJob.current !== id) return;
    try {
      const { data } = await api.get(`${jobsUrl}/${id}`);
      if (!mounted.current || activeJob.current !== id) return;
      setJob(data);
      if (data.status === "queued" || data.status === "running") timer.current = setTimeout(() => poll(id), 1500);
    } catch (e) {
      if (!mounted.current || activeJob.current !== id) return;
      const status = e?.response?.status;
      if (status === 404 || status === 410) {
        setJob(j => ({ ...(j || { id }), status: "expired", error: "انتهت صلاحية المهمة؛ أعد التوليد بنفس الإعدادات." }));
      } else if (status === 403 || failures >= 4) {
        setJob(j => ({ ...(j || { id }), status: "failed", error: status === 403 ? "تغير نطاق صلاحيتك؛ أعد التوليد." : "تعذر متابعة التوليد. أعد المحاولة." }));
      } else {
        setJob(j => ({ ...(j || { id, status: "running", progress: 0 }), stage: "انقطع الاتصال مؤقتًا؛ جارٍ استعادة حالة التقرير" }));
        timer.current = setTimeout(() => poll(id, failures + 1), 2500);
      }
    }
  };
  useEffect(() => {
    mounted.current = true;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const j = JSON.parse(saved); activeJob.current = j.id;
        setJob({ id: j.id, status: "running", progress: 0, stage: "استعادة حالة التقرير" });
        setStep(4); if (j.state) setS({ ...EMPTY, ...j.state }); poll(j.id);
      } catch { localStorage.removeItem(storageKey); }
    }
    return () => { mounted.current = false; activeJob.current = null; clearTimeout(timer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (body = payload) => {
    if (body.kind === "custom" && !body.sections.length) return toast.error("اختر محورًا واحدًا على الأقل");
    setBusy(true);
    try {
      const { data } = await api.post(jobsUrl, body);
      localStorage.setItem(storageKey, JSON.stringify({ id: data.id, state: s }));
      clearTimeout(timer.current); activeJob.current = data.id; setJob(data); setStep(4); poll(data.id);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const regenerate = () => generate(job?.request ? { ...job.request } : payload);
  const reset = () => { clearTimeout(timer.current); activeJob.current = null; localStorage.removeItem(storageKey); setJob(null); setS(EMPTY); setStep(0); };

  if (optionsError) return <GlassCard className="p-6">تعذر تحميل إعدادات التقرير. <button className="v2-btn-ghost v2-btn-sm" onClick={() => window.location.reload()}>إعادة المحاولة</button></GlassCard>;
  if (!opt) return <Skeleton className="h-96 w-full" />;
  const chipList = chips(opt, s.filters);
  const canNext = step === 3 ? (s.kind === "full" || s.sections.length > 0) : true;
  const Icon = icon;

  return (
    <div className="space-y-5" data-testid={testId}>
      <PageHead icon={Icon} title={title} sub={`${sub} (${fmtNum(opt.users_in_scope)} موظفًا في النطاق).`}>
        {(job || step > 0) && <button onClick={reset} className="v2-btn-ghost v2-btn-sm whitespace-nowrap" data-testid="report-new"><X size={13} /> تقرير جديد</button>}
      </PageHead>
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="report-steps">
        {STEPS.map(([k, l, I], i) => (
          <button key={k} onClick={() => i < 4 && setStep(i)} disabled={i === 4 && !job} data-testid={`step-tab-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs font-bold transition-all ${step === i ? "bg-beige text-ink" : i < step ? "bg-ok/15 text-ok border border-ok/30" : "bg-surface-2 text-ink-2 border border-line"}`}>
            <I size={14} /> {i + 1}. {l}
          </button>
        ))}
      </div>
      {chipList.length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="report-filter-chips">{chipList.map((x) => <Chip key={x} tone="gold">{x}</Chip>)}</div>}
      <GlassCard className="p-5 sm:p-6">
        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="step-kind">
            {kinds.map(([k, l, d]) => (
              <button key={k} onClick={() => setS({ ...s, kind: k })} data-testid={`rk-${k}`} className={`text-start v2-inset p-6 transition-all ${s.kind === k ? "!border-beige/70 !bg-beige/12" : "hover:!border-line"}`}>
                <div className="flex items-center gap-2 mb-1">{s.kind === k ? <CheckCircle2 className="text-beige-ink" size={18} /> : <FileText className="text-ink-2" size={18} />}<span className="text-lg font-extrabold">{l}</span></div>
                <div className="text-sm text-ink-2 leading-relaxed">{d}</div>
              </button>
            ))}
          </div>
        )}
        {step === 1 && <div className="space-y-4" data-testid="step-scope"><div className="text-sm text-ink-2">الفلاتر تُجمع معًا. النطاق يُطبَّق في الخادم ولا يتجاوز صلاحيتك، وتُصدَّر جميع النتائج المطابقة.</div><div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{renderScope({ f: s.filters, setF, opt })}</div></div>}
        {step === 2 && <PeriodStep mode={s.period_mode} setMode={(m) => setS({ ...s, period_mode: m })} f={s.filters} setF={setF} />}
        {step === 3 && <SectionsStep s={s} setS={setS} sections={sections(opt)} fullDesc={fullDesc} />}
        {step === 4 && <ResultPanel job={job} fileBase={fileBase} onRegenerate={regenerate} busy={busy} />}
        {step < 4 && (
          <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="v2-btn-ghost v2-btn-sm whitespace-nowrap disabled:opacity-40" data-testid="report-prev"><ChevronRight size={14} /> السابق</button>
            {step < 3 ? <button onClick={() => setStep(step + 1)} className="v2-btn v2-btn-sm whitespace-nowrap" data-testid="report-next">التالي <ChevronLeft size={14} /></button>
              : <button onClick={() => generate()} disabled={busy || !canNext} className="v2-btn" data-testid="report-generate">{busy ? <RefreshCw size={15} className="animate-spin" /> : <Eye size={15} />} معاينة التقرير وتوليد PDF</button>}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
